// © Thibault Sabathier, 2026. Tous droits réservés.
// frontend/src/utils/memberDirectoryFilters.js
//
// Prédicats de filtrage de l'annuaire pour Promo/Diplôme — extraits de
// `views/members/Members.js` pour être réutilisables en vue carte ET
// tableau, et testables sans DOM (même convention que
// utils/schoolRoles.js/utils/memberType.js : matchesXFilter(member, value),
// valeur vide = pas de filtre actif = tout passe — jamais un filtre non
// renseigné ne doit vider l'annuaire).
//
// Promo et Diplôme sont deux filtres DISTINCTS et INDÉPENDANTS (combinables
// via ET, comme tout le reste de `filters`) — Promo ne matche que sur
// l'année, jamais sur le programme : le combiner dans le même filtre
// dupliquait l'information déjà couverte par le filtre Diplôme voisin
// (retour utilisateur).
//
// Les deux prédicats matchent sur `member.primaryEducation` (texte libre
// renvoyé par le backend — school/program/degree/endYear), jamais sur un
// program_id structuré : celui-ci n'est pas exposé au frontend aujourd'hui
// (cf. backend/routes/member.js, attributes de l'include MemberEducation).
// Depuis le correctif "scope école du tenant" (member.js, GET /members et
// GET /members/:id via buildEducationScope), `primaryEducation` peut être
// `null` pour un membre dont la formation principale n'est pas à l'école de
// CE tenant — les deux prédicats gèrent ce cas nativement (chaînage
// optionnel), jamais de crash.

import { normalizeString } from './filterUtils'

/**
 * Filtre Promo — valeur = année (venant du select, options dérivées des
 * membres chargés). Ne matche QUE l'année ; le programme est le rôle du
 * filtre Diplôme (matchesProgramFilter), à combiner séparément si besoin.
 * @param {object|null} member
 * @param {string} rawFilterValue
 * @returns {boolean}
 */
export function matchesPromotionFilter(member, rawFilterValue) {
  const filterValue = rawFilterValue ? rawFilterValue.toString().trim() : ''
  if (!filterValue) return true

  const endYear = member?.primaryEducation?.endYear
  return endYear ? String(endYear) === filterValue : false
}

/**
 * Filtre Diplôme/Filière — valeur = `primaryEducation.program` d'un membre
 * (texte libre, dérivé des membres chargés — pas de FK program_id côté
 * frontend), matché en texte normalisé.
 * @param {object|null} member
 * @param {string} rawFilterValue
 * @returns {boolean}
 */
export function matchesProgramFilter(member, rawFilterValue) {
  const filterValue = rawFilterValue ? normalizeString(rawFilterValue.toString().trim()) : ''
  if (!filterValue) return true

  const program = member?.primaryEducation?.program || ''
  const normalizedProgram = normalizeString(program.trim().replace(/\s+/g, ' '))
  return normalizedProgram === filterValue
}

/**
 * Options du select Diplôme, dérivées de membersData et resserrées par la
 * promo déjà sélectionnée le cas échéant (resserrement MUTUEL avec
 * buildPromotionSelectOptions ci-dessous — retour utilisateur). Chaque
 * option affiche entre parenthèses le nombre de membres concernés PAR
 * L'ÉTAT ACTUEL du filtre Promo.
 * @param {Array<object>} membersData
 * @param {string} selectedPromotionValue — filters.promotion (année en
 *   chaîne, ou '' si aucune promo sélectionnée)
 * @returns {{value: string, label: string}[]} triées alphabétiquement
 */
export function buildProgramSelectOptions(membersData, selectedPromotionValue) {
  const byKey = new Map()
  for (const m of membersData || []) {
    const edu = m?.primaryEducation
    const program = (edu?.program || '').trim().replace(/\s+/g, ' ')
    if (!program) continue
    if (selectedPromotionValue && String(edu.endYear || '') !== selectedPromotionValue) continue
    const key = normalizeString(program)
    const existing = byKey.get(key)
    if (existing) existing.count += 1
    else byKey.set(key, { value: program, count: 1 })
  }
  return Array.from(byKey.values())
    .map((o) => ({ value: o.value, label: `${o.value} (${o.count})` }))
    .sort((a, b) => a.value.localeCompare(b.value))
}

/**
 * Options du select Promo, dérivées de membersData et resserrées par le
 * diplôme déjà sélectionné le cas échéant (resserrement MUTUEL avec
 * buildProgramSelectOptions ci-dessus). `formatYearLabel` découple ce
 * helper de i18n (pur JS, testable sans wrapper i18next) — le composant lui
 * passe `(year) => t('members:filter.promotion_year', { year })`.
 * @param {Array<object>} membersData
 * @param {string} selectedProgramValue — filters.program, ou '' si aucun
 *   diplôme sélectionné
 * @param {(year: number) => string} formatYearLabel
 * @returns {{value: string, label: string, year: number}[]} triées année décroissante
 */
export function buildPromotionSelectOptions(membersData, selectedProgramValue, formatYearLabel) {
  const byYear = new Map()
  const programFilterValue = selectedProgramValue ? normalizeString(selectedProgramValue) : ''
  for (const m of membersData || []) {
    const edu = m?.primaryEducation
    const year = edu?.endYear
    if (!year) continue
    if (programFilterValue) {
      const memberProgram = normalizeString((edu.program || '').trim().replace(/\s+/g, ' '))
      if (memberProgram !== programFilterValue) continue
    }
    const existing = byYear.get(year)
    if (existing) existing.count += 1
    else byYear.set(year, { year, count: 1 })
  }
  return Array.from(byYear.values())
    .map((o) => ({
      value: String(o.year),
      label: `${formatYearLabel(o.year)} (${o.count})`,
      year: o.year,
    }))
    .sort((a, b) => b.year - a.year)
}

// ─── « Je peux aider » (lot 3, L3a) ─────────────────────────────────────────

/**
 * Le membre a-t-il indiqué pouvoir faire des mises en relation ? Valeur STRICTE `true` : le serveur ne renvoie le
 * champ que si le profil est découvrable (ou pour soi-même) ; son absence, `null`, `0` ou `'1'` valent « non ».
 * @param {object|null} member
 */
export function isReferralOpen(member) {
  return Boolean(member) && member.referral_openness === true
}

/**
 * Filtre « Peut faire des mises en relation » : désactivé = tout passe (un filtre non coché ne vide jamais l'annuaire).
 * @param {object|null} member
 * @param {boolean} enabled
 */
export function matchesReferralOpenFilter(member, enabled) {
  return !enabled || isReferralOpen(member)
}
